// VIBE-1 — **REPORTING A PURCHASE TO THE ADVERTISING PIXEL**, pulled out of App.tsx by R8's own
// entry requirement: a law inside a TSX file cannot be imported by a .mjs gate, so the sequence that
// decides whether her TV campaign sees a conversion would have had no instrument at all.
//
// `doFetch` and `win` are PARAMETERS rather than reaches for the globals (seal-run.ts and
// locked-door.ts's repair, for the same reason): a gate RUNS this against a fake fetch and a fake
// window and reads back exactly what was reported, with no browser and no network.
import { sendPixelEvent, purchasePayload } from "../vibePixel";

export type PendingPurchase = { id: number; kind: string; amountCents: number | null; currency: string; purchaseId?: string };

type Deps = {
  doFetch: typeof fetch;
  win: unknown;
  stillActive?: () => boolean;
};

// CLAIM FIRST, THEN FIRE — the order IS the exactly-once guarantee, and it is the one thing in here
// that must not be "simplified". The POST is an atomic `UPDATE ... WHERE reported_at IS NULL
// RETURNING id`, so a reload, the back button, or two tabs open on the same account all lose the
// race and are handed an empty list. Firing first and acking afterwards would re-report every
// purchase whose ack was lost — the direction that quietly inflates the number her spend is
// optimised against, and the direction nobody checks because a metric that is too high reads as
// success.
export async function reportPendingPurchases({ doFetch, win, stillActive }: Deps): Promise<number[]> {
  const active = () => (stillActive ? stillActive() : true);
  try {
    const pending = await doFetch("/api/pixel/purchases", { credentials: "include" }).then((r) => r.json());
    if (!active() || !pending?.ok || !Array.isArray(pending.purchases) || !pending.purchases.length) return [];

    const claim = await doFetch("/api/pixel/purchases", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ids: (pending.purchases as PendingPurchase[]).map((p) => p.id) }),
    }).then((r) => r.json());
    if (!active() || !claim?.ok || !Array.isArray(claim.reported)) return [];

    const won = new Set<number>(claim.reported);
    const fired: number[] = [];
    for (const p of pending.purchases as PendingPurchase[]) {
      if (!won.has(p.id)) continue;                      // another tab claimed this one
      // `purchasePayload` answers null for an unknown amount and `sendPixelEvent` turns that into
      // the BARE event — her own fallback rule, and what the portal upgrade path uses because that
      // event carries no charged amount to be honest about.
      const sent = sendPixelEvent(win, "purchase", purchasePayload({ amountCents: p.amountCents, purchaseId: p.purchaseId }));
      if (sent.sent) fired.push(p.id);
    }
    return fired;
  } catch {
    // A marketing signal never disturbs the member and never breaks a render. An unclaimed row is
    // simply reported on the next load.
    return [];
  }
}
